import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import { FiBarChart2 as BarChart3, FiFolder as Folder, FiLayers as Layers, FiLifeBuoy as Ticket, FiClipboard as ClipboardList, FiHome as Building2, FiDollarSign as DollarSign, FiActivity as Activity, FiHelpCircle as HelpCircle, FiBell } from 'react-icons/fi';
import HubGettingStartedBanner from './HubGettingStartedBanner';
import { useHubOnboardingStatus } from '../../../hooks/useHubOnboardingStatus';
import HelpPanel from '../../../components/common/HelpPanel';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { HubHelp as HubHelpJS } from '../../../features/hub/help';
const HubHelp = HubHelpJS as unknown as React.ComponentType<{ activePage: string }>;

type ActivePage = 'dashboard' | 'projects' | 'workloads' | 'support' | 'assessments' | 'clients' | 'aws-environments' | 'funding' | 'notifications' | 'tasks';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const HubLayout = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const { isComplete: onboardingComplete, refresh: refreshOnboarding } = useHubOnboardingStatus();
  const [showGettingStarted, setShowGettingStarted] = useState(() => {
    return localStorage.getItem('hub_onboarding_dismissed') !== 'true';
  });
  const [showHelpPanel, setShowHelpPanel] = useState(false);

  // Refresh onboarding status on route change to pick up newly completed steps
  useEffect(() => {
    refreshOnboarding();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-dismiss when all onboarding steps are complete
  useEffect(() => {
    if (onboardingComplete) {
      localStorage.setItem('hub_onboarding_dismissed', 'true');
      setShowGettingStarted(false);
    }
  }, [onboardingComplete]);

  // Determine the active page based on the current path
  const getActivePage = (): ActivePage => {
    const path = location.pathname;
    if (path.startsWith('/hub/workloads')) return 'workloads';
    if (path.startsWith('/hub/projects')) return 'projects';
    if (path.startsWith('/hub/support')) return 'support';
    if (path.startsWith('/hub/assessments')) return 'assessments';
    if (path.startsWith('/hub/clients')) return 'clients';
    if (path.startsWith('/hub/aws-environments')) return 'aws-environments';
    if (path.startsWith('/hub/funding')) return 'funding';
    if (path.startsWith('/hub/notifications')) return 'notifications';
    return 'dashboard';
  };

  // Dynamic help panel title
  const helpPanelTitle = useMemo(() => {
    const activePage = getActivePage();
    const titles: Record<ActivePage, string> = {
      dashboard: 'Dashboard Help',
      projects: 'Projects Help',
      workloads: 'Workloads Help',
      support: 'Support Help',
      assessments: 'Assessments Help',
      clients: 'Clients Help',
      'aws-environments': 'AWS Environments Help',
      funding: 'Funding Help',
      notifications: 'Notifications Help',
      tasks: 'Tasks Help',
    };
    return titles[activePage] || 'Help';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleDismissGettingStarted = () => {
    setShowGettingStarted(false);
    localStorage.setItem('hub_onboarding_dismissed', 'true');
  };

  const handleShowGettingStarted = () => {
    localStorage.removeItem('hub_onboarding_dismissed');
    setShowGettingStarted(true);
  };

  const isClientUser = Boolean(user?.client_id);

  const hubNavItems = useMemo((): NavItem[] => {
    const items: NavItem[] = [
      { path: '/hub/dashboard', label: 'Dashboard', icon: BarChart3 },
    ];

    // Projects - accessible if user has projects.view permission
    if (hasPermission(PERMISSIONS.PROJECTS_VIEW)) {
      items.push({ path: '/hub/projects', label: 'Projects', icon: Folder });
      items.push({ path: '/hub/workloads', label: 'Workloads', icon: Layers });
    }

    // Support - accessible to all authenticated users
    items.push({ path: '/hub/support', label: 'Support', icon: Ticket });

    // Assessments - check permission
    if (hasPermission(PERMISSIONS.ASSESSMENTS_VIEW)) {
      items.push({ path: '/hub/assessments', label: 'Assessments', icon: ClipboardList });
    }

    // Clients - accessible if user has clients permissions
    if (hasPermission(PERMISSIONS.CLIENTS_VIEW) || hasPermission(PERMISSIONS.CLIENTS_MANAGE)) {
      items.push({ path: '/hub/clients', label: 'Clients', icon: Building2 });
    }

    // Funding - only visible to AWS and Partner users, not Client users
    if (!isClientUser &&
        (hasPermission(PERMISSIONS.FINANCE_VIEW) ||
        hasPermission(PERMISSIONS.FINANCE_EDIT) ||
        hasPermission(PERMISSIONS.FINANCE_MANAGE))) {
      items.push({ path: '/hub/funding', label: 'Funding', icon: DollarSign });
    }

    // Notifications - accessible to all authenticated users
    items.push({ path: '/hub/notifications', label: 'Notifications', icon: FiBell });

    // Task executions (ops) - system admin only
    if (isAdmin || hasPermission(PERMISSIONS.SYSTEM_ADMIN)) {
      items.push({ path: '/hub/tasks', label: 'Tasks', icon: Activity });
    }

    return items;
  }, [hasPermission, isAdmin, isClientUser]);

  const isActive = (path: string): boolean => {
    return location.pathname === path || (path === '/hub/dashboard' && location.pathname === '/hub');
  };

  return (
    <div>
      {/* Hub Sub-Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <nav className="flex overflow-x-auto space-x-4 sm:space-x-8 -mb-px scrollbar-hide" aria-label="Hub navigation">
              {hubNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`${
                      isActive(item.path)
                        ? 'border-blue-500 text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors flex-shrink-0`}
                  >
                    <Icon className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="flex items-center gap-2">
              {/* Help button to show Getting Started guide (clients only, hidden when complete) */}
              {isClientUser && !showGettingStarted && !onboardingComplete && (
                <button
                  onClick={handleShowGettingStarted}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title="Show Getting Started guide"
                >
                  <span className="hidden sm:inline">Getting Started</span>
                </button>
              )}
              {/* Help Panel button */}
              <button
                onClick={() => setShowHelpPanel(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Help"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started Banner - show for client users who haven't dismissed it (except assessment_only and anonymous users) */}
      {isClientUser && showGettingStarted && user?.client?.subscription_plan !== 'assessment_only' && !user?.is_anonymous && (
        <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <HubGettingStartedBanner onDismiss={handleDismissGettingStarted} />
        </div>
      )}

      {/* Hub Content */}
      <Outlet />

      {/* Help Panel */}
      <HelpPanel
        isOpen={showHelpPanel}
        onClose={() => setShowHelpPanel(false)}
        title={helpPanelTitle}
      >
        <HubHelp activePage={getActivePage()} />
      </HelpPanel>
    </div>
  );
};

export default HubLayout;
